import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, DatePicker, Switch } from 'antd';
import { userService } from '../../services/userService';
import { deviceService } from '../../services/deviceService';
import { User } from '@/types/User';
import dayjs from 'dayjs';

interface ApiResponse {
    req: {
        page: string;
        limit: string;
        search: string;
    };
    data: User[];
    total: number;
    currentPage: number;
    totalPages: number;
}

const UserManagement: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isDeviceModalVisible, setIsDeviceModalVisible] = useState(false);
    const [selectedDevices, setSelectedDevices] = useState<any[]>([]);
    const [currentUser, setCurrentUser] = useState<Partial<User>>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalUsers, setTotalUsers] = useState(0);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [dateRange, setDateRange] = useState<any>(null);

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const response: ApiResponse = await userService.getUsers({
                page: currentPage,
                pageSize: pageSize,
                search: search,
                from: from,
                to: to
            });

            setUsers(response.data);
            setTotalUsers(response.total);
        } catch (error) {
            message.error('Không thể tải danh sách người dùng');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, search, from, to]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70
        },
        {
            title: 'Tên người dùng',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Vai trò',
            dataIndex: 'role',
            key: 'role',
            render: (role: string) => (
                <span className={role === 'admin' ? 'text-red-500' : 'text-blue-500'}>
                    {role}
                </span>
            )
        },
        {
            title: 'Ngày tạo',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => new Date(date).toLocaleDateString('vi-VN')
        },
        {
            title: 'Ngày hết hạn',
            dataIndex: 'expiredDate',
            key: 'expiredDate',
            render: (date: string) => date ? new Date(date).toLocaleDateString('vi-VN') : '-'
        },
        {
            title: 'Thiết bị',
            dataIndex: 'Devices',
            key: 'Devices',
            render: (Devices: any) => {
                return (
                    Devices?.length > 0 ? (
                        <p className='cursor-pointer' onClick={() => showDeviceModal(Devices)}>{Devices?.length} / 5</p>
                    ) : (
                        '0 / 5'
                    )
                )
            }
        },
        {
            title: 'Trạng Thái',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <span className={`${status === '1' ? 'text-green-500' : 'text-red-500'}`}>
                    {status === '1' ? 'Hoạt Động' : 'Ngừng'}
                </span>
            ),
        },
        {
            title: 'Hành Động',
            key: 'actions',
            render: (text: string, record: User) => (
                <div className="flex space-x-2">
                    <Button size="small" onClick={() => handleEdit(record)}>Sửa</Button>
                    <Button size="small" danger onClick={() => handleDelete(record.id)}>Xóa</Button>
                </div>
            ),
        },
    ];

    const handleEdit = (user: User) => {
        setCurrentUser(user);
        setIsModalVisible(true);
    };

    const handleDelete = async (userId: number) => {
        try {
            await userService.deleteUser(userId);
            message.success('Xóa người dùng thành công');
            fetchUsers();
        } catch (error) {
            message.error('Không thể xóa người dùng');
        }
    };

    const handleSubmit = async () => {
        try {
            if (currentUser.id) {
                await userService.updateUser(currentUser as User);
                message.success('Sửa thông tin người dùng thành công');
            } else {
                await userService.createUser(currentUser as Omit<User, 'id'>);
                message.success('Lưu người dùng mới thành công');
                setCurrentPage(1);
            }
            setIsModalVisible(false);
            fetchUsers();
        } catch (error) {
            if (currentUser.id) {
                message.error('Không thể sửa người dùng');
            } else {
                message.error('Không thể lưu người dùng');
            }
        }
    };

    const handleTableChange = (newPagination: any) => {
        setCurrentPage(newPagination.current);
        setPageSize(newPagination.pageSize);
    };

    const showDeviceModal = (devices: any[]) => {
        setSelectedDevices(devices);
        setIsDeviceModalVisible(true);
    };

    const handleDeleteDevice = async (deviceId: string) => {
        try {
            await deviceService.deleteDevice(deviceId);
            message.success('Xóa thiết bị thành công');
            const updatedDevices = selectedDevices.filter(device => device.id !== deviceId);
            setSelectedDevices(updatedDevices);
            fetchUsers();
        } catch (error) {
            message.error('Không thể xóa thiết bị');
        }
    };

    return (
        <div className="p-6">
            <div className="flex justify-between mb-4">
                <h1 className="text-2xl font-bold">Quản Lý Người Dùng</h1>
                <div className='flex gap-4'>
                    <Input
                        style={{ width: 250 }}
                        placeholder="Tìm kiếm người dùng"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <DatePicker.RangePicker
                        style={{ width: 250 }}
                        value={dateRange}
                        onChange={(dates) => {
                            if (dates) {
                                const [start, end] = dates;
                                const startDate = start ? start.format('YYYY-MM-DD 00:00:00') : '';
                                const endDate = end ? end.format('YYYY-MM-DD 23:59:59') : '';
                                setFrom(startDate);
                                setTo(endDate);
                                setDateRange(dates);
                            } else {
                                setFrom('');
                                setTo('');
                                setDateRange(null);
                            }
                        }}

                    />
                    <Button
                        type="primary"
                        onClick={() => {
                            setCurrentUser({});
                            setIsModalVisible(true);
                        }}
                    >
                        Thêm Người Dùng
                    </Button>
                </div>
            </div>

            <Table
                tableLayout='fixed'
                columns={columns}
                dataSource={users}
                rowKey="id"
                scroll={{ y: 600 }}
                pagination={{
                    size: 'small',
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalUsers,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: [5, 10, 20],
                    showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} người dùng`
                }}
                loading={loading}
                onChange={handleTableChange}
            />

            <Modal
                title={currentUser.id ? 'Sửa Người Dùng' : 'Thêm Người Dùng Mới'}
                visible={isModalVisible}
                onOk={handleSubmit}
                onCancel={() => setIsModalVisible(false)}
            >
                <Form layout="vertical">
                    <Form.Item label="Tên Người Dùng">
                        <Input
                            value={currentUser.name}
                            onChange={(e) => setCurrentUser({ ...currentUser, name: e.target.value })}
                        />
                    </Form.Item>
                    <Form.Item label="Email">
                        <Input
                            value={currentUser.email}
                            onChange={(e) => setCurrentUser({ ...currentUser, email: e.target.value })}
                        />
                    </Form.Item>
                    <Form.Item label={!currentUser.id ? "Mật Khẩu" : "Đặt Lại Mật Khẩu"}>
                        <Input.Password
                            value={currentUser.password}
                            onChange={(e) => setCurrentUser({ ...currentUser, password: e.target.value })}
                        />
                    </Form.Item>
                    <Form.Item label="Vai trò">
                        <Select
                            value={currentUser.role}
                            onChange={(value) => setCurrentUser({ ...currentUser, role: value as "admin" | "user" })}
                            defaultValue={!currentUser.id ? "user" : currentUser.role}
                        >
                            <Select.Option value="admin">Admin</Select.Option>
                            <Select.Option value="user">User</Select.Option>
                        </Select>
                    </Form.Item>
                    <div className='flex gap-4'>
                        <Form.Item label="Xem video">
                            <Switch
                                checked={currentUser.isViewVideo === '1'}
                                onChange={(value) => setCurrentUser({ ...currentUser, isViewVideo: value ? '1' : '0' })}
                            />
                        </Form.Item>
                        <Form.Item label="Hoạt động">
                            <Switch
                                checked={currentUser.status === '1'}
                                onChange={(value) => setCurrentUser({ ...currentUser, status: value ? '1' : '0' })}
                            />

                        </Form.Item>

                    </div>
                    <Form.Item label="Ngày hết hạn">
                        <DatePicker
                            value={currentUser.expiredDate ? dayjs(currentUser.expiredDate) : null}
                            onChange={(value) => setCurrentUser({
                                ...currentUser,
                                expiredDate: value ? value.toDate() : null
                            })}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Thông tin thiết bị"
                open={isDeviceModalVisible}
                width={500}
                onCancel={() => setIsDeviceModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsDeviceModalVisible(false)}>
                        Đóng
                    </Button>
                ]}
            >
                <div className="space-y-4">
                    {selectedDevices.map((device, index) => (
                        <div key={index} className="border p-4 rounded-md">
                            <div className="flex justify-between items-center">
                                <div>
                                    <p><strong>Địa chỉ IP:</strong> {device?.ipAddress}</p>
                                    <p><strong>Loại thiết bị:</strong> {device?.deviceType || 'Không xác định'}</p>
                                    <p><strong>Trình duyệt:</strong> {device?.browser || 'Không xác định'}</p>
                                    <p><strong>Hệ điều hành:</strong> {device?.os || 'Không xác định'}</p>
                                    <p><strong>Màn hình:</strong> {device?.screenResolution || 'Không xác định'}</p>
                                </div>
                                <div>
                                    <Button
                                        danger
                                        size="small"
                                        onClick={() => {
                                            Modal.confirm({
                                                title: 'Xác nhận xóa thiết bị',
                                                content: 'Bạn có chắc chắn muốn xóa thiết bị này?',
                                                okText: 'Xóa',
                                                cancelText: 'Hủy',
                                                onOk: () => handleDeleteDevice(device.id)
                                            });
                                        }}
                                    >
                                        Xóa
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                    {selectedDevices.length === 0 && (
                        <p>Không có thiết bị nào</p>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default UserManagement;